import { PeopleOutline, Search } from '@mui/icons-material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import {
    Avatar,
    Box,
    Chip,
    Divider,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Button } from 'src/components/forms'
import { HttpGroup } from 'src/http/group/httpGroup'
import {
    GroupDTOFields,
    GroupUserFields,
    GroupUserViewDTO,
    GroupUserListResponseDTO,
    GroupViewDTO
} from 'src/types/group/groupData'
import { GroupUserFilterDTOFields } from 'src/types/user/userData'
import { EntityWithIdFields } from 'src/types'

export type EditGroupUsersProps = {
    group: GroupViewDTO
    onClose: () => void
}

const GRID_HEIGHT = 400
const GRID_SLOTS = { columnMenuIcon: () => <></> }

const gridSx = {
    height: '100%',
    border: 0,
    '& .MuiDataGrid-columnHeaders': {
        bgcolor: 'background.default'
    },
    '& .MuiDataGrid-row:hover': {
        bgcolor: 'action.hover'
    },
    '& .MuiDataGrid-cell': {
        alignItems: 'center',
        py: 0.75
    }
}

const UserCell = ({ row }: { row: GroupUserViewDTO }) => {
    const fullName = row[GroupUserFields.FullName]
    const mail = row[GroupUserFields.Mail]
    const initials = (fullName ?? '')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0])
        .join('')
        .toUpperCase()

    return (
        <Stack direction='row' spacing={1.5} alignItems='center' sx={{ minWidth: 0, width: '100%' }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 13 }}>{initials || '?'}</Avatar>
            <Box sx={{ minWidth: 0 }}>
                <Tooltip title={fullName || 'Sin nombre'} placement='top-start'>
                    <Typography
                        variant='body2'
                        fontWeight={600}
                        noWrap
                        sx={{ color: fullName ? 'text.primary' : 'text.disabled' }}
                    >
                        {fullName || 'Sin nombre'}
                    </Typography>
                </Tooltip>
                <Tooltip title={mail || 'Sin email'} placement='top-start'>
                    <Typography
                        variant='caption'
                        color={mail ? 'text.secondary' : 'text.disabled'}
                        noWrap
                        sx={{ display: 'block' }}
                    >
                        {mail || 'Sin email'}
                    </Typography>
                </Tooltip>
            </Box>
        </Stack>
    )
}

function EditGroupUsers({ group, onClose }: EditGroupUsersProps) {
    const [assignedIds, setAssignedIds] = useState<number[] | undefined>(undefined)
    const [filterNombre, setFilterNombre] = useState('')
    const [filterMail, setFilterMail] = useState('')
    const [leftSelected, setLeftSelected] = useState<number[]>([])
    const [rightSelected, setRightSelected] = useState<number[]>([])
    const rightUsersMapRef = useRef<Map<number, GroupUserViewDTO>>(new Map())

    const { data: gruposData, isLoading, refetch } = useQuery<GroupUserListResponseDTO>({
        queryKey: ['group-users-transfer', group[EntityWithIdFields.Id]],
        queryFn: () =>
            HttpGroup.getUsersByGroup(group[EntityWithIdFields.Id], {
                [GroupUserFilterDTOFields.SoloActivos]: true
            }),
        staleTime: 0,
        gcTime: 0,
        refetchOnMount: 'always'
    })

    useEffect(() => {
        if (!gruposData?.asignados) return
        gruposData.asignados.forEach(u => rightUsersMapRef.current.set(u[GroupUserFields.UserId], u))
        if (assignedIds === undefined) setAssignedIds(gruposData.asignados.map(u => u[GroupUserFields.UserId]))
    }, [gruposData])

    const assignedSet = new Set(assignedIds ?? [])

    const applyFilters = (users: GroupUserViewDTO[]) => {
        let result = users
        if (filterNombre)
            result = result.filter(u => u[GroupUserFields.FullName]?.toLowerCase().includes(filterNombre.toLowerCase()))
        if (filterMail)
            result = result.filter(u => u[GroupUserFields.Mail]?.toLowerCase().includes(filterMail.toLowerCase()))
        return result
    }

    const leftUsers = applyFilters(
        (gruposData?.disponibles ?? []).filter(u => !assignedSet.has(u[GroupUserFields.UserId]))
    )

    const rightUsers = applyFilters(
        (assignedIds ?? [])
            .map(id => rightUsersMapRef.current.get(id))
            .filter((u): u is GroupUserViewDTO => u !== undefined)
    )

    const moveRight = () => {
        if (leftSelected.length === 0) return
        leftUsers
            .filter(u => leftSelected.includes(u[GroupUserFields.UserId]))
            .forEach(u => rightUsersMapRef.current.set(u[GroupUserFields.UserId], u))
        setAssignedIds(prev => [...new Set([...(prev ?? []), ...leftSelected])])
        setLeftSelected([])
    }

    const moveLeft = () => {
        if (rightSelected.length === 0) return
        const removing = new Set(rightSelected)
        setAssignedIds(prev => (prev ?? []).filter(id => !removing.has(id)))
        setRightSelected([])
    }

    const columns: GridColDef<GroupUserViewDTO>[] = [
        {
            headerName: 'Usuario',
            field: GroupUserFields.FullName,
            flex: 1,
            minWidth: 320,
            sortable: false,
            disableColumnMenu: true,
            renderCell: ({ row }) => <UserCell row={row} />
        }
    ]

    const onSubmit = () => {
        let mensajeError = 'No se pudieron guardar los usuarios'
        const promise = HttpGroup.updateGroupUsers(group[EntityWithIdFields.Id], assignedIds ?? [])
            .then(response => refetch().then(() => response))
            .catch(err => {
                mensajeError = err?.message || mensajeError
                throw err
            })
        toast.promise(promise, {
            success: 'Usuarios actualizados',
            error: () => mensajeError,
            loading: 'Guardando'
        })
        return promise
    }

    const groupName = group[GroupDTOFields.Name] ?? 'grupo'

    return (
        <>
            <DialogTitle sx={{ px: 3, py: 2.5 }}>
                <Stack direction='row' spacing={2} alignItems='center'>
                    <Avatar sx={{ width: 42, height: 42, bgcolor: 'primary.main' }}>
                        <PeopleOutline fontSize='small' />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant='h6' fontWeight={700} lineHeight={1.25}>
                            Usuarios de {groupName}
                        </Typography>
                        <Typography variant='body2' color='text.secondary' noWrap>
                            Gestiona los usuarios vinculados al grupo
                        </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Chip color='primary' variant='outlined' label={`${rightUsers.length} asignados`} size='small' />
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ pt: '0 !important', px: 3, pb: 0 }}>
                <Divider sx={{ mb: 2 }} />

                {/* Filtros compartidos */}
                <Paper
                    variant='outlined'
                    sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: 1,
                        bgcolor: 'background.default'
                    }}
                >
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <TextField
                            value={filterNombre}
                            onChange={e => setFilterNombre(e.target.value)}
                            label='Usuario'
                            size='small'
                            sx={{ flex: 1 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <Search fontSize='small' />
                                    </InputAdornment>
                                )
                            }}
                        />
                        <TextField
                            value={filterMail}
                            onChange={e => setFilterMail(e.target.value)}
                            label='Email'
                            size='small'
                            sx={{ flex: 1 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <Search fontSize='small' />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Stack>
                </Paper>

                {/* Transfer list */}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems='stretch'>
                    {/* Lista izquierda: disponibles */}
                    <Paper variant='outlined' sx={{ flex: 1, minWidth: 0, overflow: 'hidden', borderRadius: 1 }}>
                        <Stack direction='row' alignItems='center' spacing={1.5} sx={{ px: 2, py: 1.5 }}>
                            <PeopleOutline color='action' fontSize='small' />
                            <Typography variant='subtitle2' fontWeight={700}>
                                Disponibles
                            </Typography>
                            <Chip size='small' label={leftUsers.length} variant='outlined' />
                        </Stack>
                        <Divider />
                        <Box sx={{ p: 1.5 }}>
                            <Box sx={{ height: GRID_HEIGHT, width: '100%' }}>
                                <DataGrid
                                    rows={leftUsers}
                                    columns={columns}
                                    getRowId={row => row[GroupUserFields.UserId]}
                                    checkboxSelection
                                    rowSelectionModel={leftSelected}
                                    onRowSelectionModelChange={model => setLeftSelected(model as number[])}
                                    loading={isLoading}
                                    density='standard'
                                    rowHeight={62}
                                    columnHeaderHeight={44}
                                    hideFooter
                                    disableColumnFilter
                                    disableColumnSelector
                                    slots={GRID_SLOTS}
                                    sx={gridSx}
                                />
                            </Box>
                        </Box>
                    </Paper>

                    {/* Botones del medio */}
                    <Stack
                        justifyContent='center'
                        alignItems='center'
                        spacing={1}
                        sx={{
                            px: { xs: 0, md: 0.5 },
                            py: { xs: 0, md: 8 },
                            flexDirection: { xs: 'row', md: 'column' }
                        }}
                    >
                        <Tooltip title='Agregar seleccionados'>
                            <span>
                                <IconButton
                                    color='primary'
                                    disabled={leftSelected.length === 0}
                                    onClick={moveRight}
                                    sx={{
                                        border: theme => `1px solid ${theme.palette.divider}`,
                                        bgcolor: 'background.paper'
                                    }}
                                >
                                    <ArrowForwardIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title='Quitar seleccionados'>
                            <span>
                                <IconButton
                                    color='error'
                                    disabled={rightSelected.length === 0}
                                    onClick={moveLeft}
                                    sx={{
                                        border: theme => `1px solid ${theme.palette.divider}`,
                                        bgcolor: 'background.paper'
                                    }}
                                >
                                    <ArrowBackIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>

                    {/* Lista derecha: asignados */}
                    <Paper variant='outlined' sx={{ flex: 1, minWidth: 0, overflow: 'hidden', borderRadius: 1 }}>
                        <Stack direction='row' alignItems='center' spacing={1.5} sx={{ px: 2, py: 1.5 }}>
                            <PeopleOutline color='primary' fontSize='small' />
                            <Typography variant='subtitle2' fontWeight={700}>
                                Asignados
                            </Typography>
                            <Chip size='small' label={rightUsers.length} color='primary' variant='outlined' />
                        </Stack>
                        <Divider />
                        <Box sx={{ p: 1.5 }}>
                            <Box sx={{ height: GRID_HEIGHT, width: '100%' }}>
                                <DataGrid
                                    rows={rightUsers}
                                    columns={columns}
                                    getRowId={row => row[GroupUserFields.UserId]}
                                    checkboxSelection
                                    rowSelectionModel={rightSelected}
                                    onRowSelectionModelChange={model => setRightSelected(model as number[])}
                                    loading={assignedIds === undefined}
                                    density='standard'
                                    rowHeight={62}
                                    columnHeaderHeight={44}
                                    hideFooter
                                    disableColumnFilter
                                    disableColumnSelector
                                    slots={GRID_SLOTS}
                                    sx={gridSx}
                                />
                            </Box>
                        </Box>
                    </Paper>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: theme => `1px solid ${theme.palette.divider}` }}>
                <Button variant='text' color='inherit' onClick={onClose}>
                    Cerrar
                </Button>
                <Button variant='contained' color='primary' promise={onSubmit}>
                    Guardar
                </Button>
            </DialogActions>
        </>
    )
}

export default EditGroupUsers
